import { color, Common, device, Stream, utils } from '@stockbit/ui';
import { Skeleton } from 'antd';
import Image from 'global/Image';
import { ConversationDetailSizes } from 'hooks/useStreamConversationModal';
import styled, { css, keyframes } from 'styled-components';

const { Flex, Modal, IconWrapper } = Common;
const { Table: TableDefault } = Stream;
const { hexOpacity: percentToHex } = utils;

interface ReplyTextWrapperProps {
  expanded?: boolean;
}

interface ReplyContentProps {
  isSelected?: boolean;
}

interface ContentWrapperProps {
  height: string;
}

interface PostContainerProps {
  dimension: {
    height: ConversationDetailSizes;
    width: ConversationDetailSizes;
  };
}

const ModalContainer = styled(Flex)`
  flex-direction: column;
  background-color: ${color.snow};
  height: 100vh;
  @media ${device.tablet} {
    background: transparent;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;
const ModalReply = styled(Modal)`
  padding-bottom: 0px !important;
  margin: 0;
  max-width: unset;
  .ant-modal-content {
    background-color: transparent;
  }
  .ant-modal-body {
    margin-top: 60px;
  }
  @media ${device.tablet} {
    .ant-modal-body {
      margin-top: 0px;
      word-wrap: unset;
    }
  }
`;
const StreamContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  height: max-content;
  @media ${device.tablet} {
    height: 100%;
    width: calc(100% - 350px);
    background: ${color.black}${percentToHex(45)};
  }
  @media ${device.laptop} {
    width: calc(100% - 440px);
  }
`;
const PostContainer = styled.div<PostContainerProps>`
  position: relative;
  background-color: ${color.light};
  border-radius: 3px;
  overflow: hidden;
  width: ${({ dimension }) => dimension.width}px;
  height: max-content;
  max-height: ${({ dimension }) => dimension.height}px;
  transform: translateY(-50%);
  top: 50%;
`;
const CloseWrapper = styled(IconWrapper)`
  display: block;
  position: absolute;
  right: 20px;
  top: 10px;
  background-color: ${color.highempahis};
  border-radius: 50%;
  padding: 10px;
  z-index: 1000;
  p {
    font-size: 8px;
  }

  @media ${device.tablet} {
    background-color: ${color.dimgray};
    position: absolute;
    right: 25px;
    top: 25px;
    z-index: inherit;
    p {
      font-size: 14px;
      :hover {
        color: ${color.light};
      }
    }
  }
`;
const ContentWrapper = styled.div<ContentWrapperProps>`
  max-height: 494px;
  overflow-y: auto;
  /* margin-top: 20px; */
  height: ${({ height }) => height};

  @media ${device.tablet} {
    margin-left: 70px;
    padding-right: 20px;
  }
`;

const HeaderContent = styled(Flex).attrs({ justify: 'space-between' })`
  width: 100%;
  margin-left: 10px;

  @media ${device.tablet} {
    margin-left: 20px;
  }
`;

const ReplyContainer = styled(Flex).attrs({ direction: 'column' })`
  background-color: ${color.snow};
  width: 100%;
  overflow: hidden;
  @media ${device.mobile} {
    overflow: unset;
  }
  @media ${device.tablet} {
    background-color: ${color.light};
    height: 100%;
    width: 350px;
  }
  @media ${device.laptop} {
    width: 440px;
  }
`;

const ReplyListContainer = styled(Flex).attrs({
  style: {
    overflowY: 'auto',
    height: '100%',
    width: '100%',
  },
  direction: 'column',
})`
  padding: 20px;
  margin-bottom: 80px;
  @media ${device.tablet} {
    padding: 0px;
    margin-bottom: 0px;
  }
`;

const HighlightAnimation = keyframes`
  0% {
    background-color: white;
  }
  20% {
    background-color: #fcf8e3;
  }
`;

const ReplyContent = styled.div<ReplyContentProps>`
  background: ${color.light};
  padding: 20px;
  width: 100%;
  position: relative;
  ${({ isSelected }) =>
    isSelected &&
    css`
      animation: ${HighlightAnimation} 3s ease-out forwards;
    `}
`;
const ReplyNestedContent = styled.div`
  border: 1px solid ${color.gainsboro};
  padding: 10px;
  position: relative;
  margin-top: 10px;
  margin-bottom: 10px;
  border-radius: 3px;
`;
const ReplyTextWrapper = styled.div<ReplyTextWrapperProps>`
  display: -webkit-box;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -moz-line-clamp: 2;
  -webkit-box-orient: vertical;
  -moz-box-orient: vertical;

  ${({ expanded }) =>
    expanded &&
    css`
      margin-bottom: 0px;
      -webkit-line-clamp: unset;
      -moz-line-clamp: unset;
    `}
`;
const CloseReplyNestedContent = styled(IconWrapper)`
  cursor: pointer;
  position: absolute;
  right: 12px;
  top: 12px;
`;
const ImageWrapper = styled.div`
  background: #333;
  display: flex;
  justify-content: center;
  max-height: 336px;
  overflow: hidden;
  position: relative;
  background-color: ${color.light};
  width: 100%;
  padding-top: 56.25%;

  && .image-convo-default {
    width: 100%;
    object-fit: cover;
    top: 0px;
    position: absolute;
    width: 100%;
    background: ${color.light};
  }

  && .image-convo-shadow {
    position: absolute;
    top: 0px;
    left: 0px;
    bottom: 0px;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    background: ${color.light};
  }

  img {
    width: 100%;
    margin: auto;
  }

  img[src$='.GIF'],
  img[src$='.gif'] {
    width: auto;
    margin: auto;
  }
`;

const ImageItemWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  max-height: 422.5px;
  margin: 12px 0;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
  }
  img[src$='.GIF'],
  img[src$='.gif'] {
    width: auto;
  }
`;

const ImgModal = styled(Image)`
  width: 100%;
  height: 90vh;
`;

const Th = styled.div`
  padding: 5px;
  height: 45px;
  border-bottom: 1px solid ${color.gainsboro};
`;
const Td = styled.div`
  padding: 5px;
  height: auto;
`;

const Table = styled(TableDefault)`
  thead {
    .ant-table-cell {
      background: ${color.light};
    }
  }
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${color.component};
  }
`;

const ItemConversationContainer = styled.div`
  margin-left: 50px;
  @media ${device.tablet} {
    margin-top: 15px;
  }
`;

const FooterContainer = styled.div`
  @media ${device.tablet} {
    margin-left: 70px;
  }
`;

const PostWrapper = styled.div`
  padding: 20px;
`;

const StreamSkeleton = styled(Skeleton).attrs({
  avatar: true,
  paragraph: { rows: 4 },
  active: true,
})`
  .ant-skeleton-title {
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    height: 20px;
  }

  .ant-skeleton-paragraph {
    margin-top: 8px !important;
    margin-bottom: 0px !important;

    li {
      height: 20px;
      margin-top: 8px !important;
    }

    li:nth-child(1) {
      width: 114px;
      margin-top: 0px;
    }

    li:nth-child(2) {
      margin-top: 20px !important;
    }
  }
`;

const ComposeConvoSkeleton = styled(Skeleton).attrs({
  avatar: true,
  paragraph: { rows: 1 },
  active: true,
})`
  padding: 20px;
  .ant-skeleton-title {
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    height: 20px;
  }

  .ant-skeleton-paragraph {
    margin-top: 8px !important;
    margin-bottom: 0px !important;

    li {
      height: 20px;
    }
  }
`;

const ImageSkeleton = styled(Skeleton.Button).attrs({
  active: true,
  block: true,
})`
  width: 100% !important;
  .ant-skeleton-button {
    height: 172px !important;
  }
`;

const LoadConvoButton = styled.div`
  background: ${color.light};
  border-bottom: 1px solid ${color.gainsboro};
  border-top: 1px solid ${color.gainsboro};
  padding: 20px;
  width: 100%;
`;

const LimitedComposeContainer = styled(Flex).attrs({
  justify: 'center',
  align: 'center',
})`
  position: fixed;
  bottom: 0;
  border-top: 1px solid ${color.gainsboro};
  background-color: ${color.light};
  width: 100%;
  height: 90px;
  @media ${device.tablet} {
    position: relative;
  }
`;

const HiddenLayerStreamConvo = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

export {
  ModalContainer,
  ModalReply,
  StreamContainer,
  PostContainer,
  CloseWrapper,
  ContentWrapper,
  ReplyContainer,
  ReplyContent,
  ReplyNestedContent,
  ImageWrapper,
  ImgModal,
  Th,
  Td,
  Table,
  CloseReplyNestedContent,
  ImageItemWrapper,
  ReplyTextWrapper,
  ItemConversationContainer,
  ReplyListContainer,
  FooterContainer,
  PostWrapper,
  StreamSkeleton,
  ImageSkeleton,
  ComposeConvoSkeleton,
  LoadConvoButton,
  LimitedComposeContainer,
  HeaderContent,
  HiddenLayerStreamConvo,
};
